import toast from 'react-hot-toast'

import {
  buildFetchOptionsWithAuth,
  fetchJson,
  useMutation,
  useQueryClient,
} from '@fv/client-core'
import type { FullShipment, MakeOptional, ShipmentTag } from '@fv/client-types'
import type { LoadTag } from '@fv/models'
import type { DTO } from '@fv/models/core'

import { apiUri, supportMessage } from '@/constants'
import {
  type UserSettings,
  userSettingsKey,
} from '@/hooks/settings/useUserSettings'

import type { ShipmentTagOption } from './useShipmentTags'

type FilterOption = {
  _id: string
  label: string
}

type AccountTag = {
  _id: string
  name: string
}
type TagRequest = {
  loadId: string
  tag: string
}

type TagResponse = {
  accountTags: AccountTag[]
  loadTags: DTO<LoadTag>[]
}

type ShipmentTags = NonNullable<FullShipment['tags']>

function mapTagResponseToShipmentTags({
  accountTags,
  loadTags,
}: TagResponse): ShipmentTags {
  return loadTags.flatMap(loadTag => {
    const accountTag = accountTags.find(a => a._id === loadTag.accountTagId)
    if (!accountTag) return []
    return [
      {
        accountTagId: loadTag.accountTagId,
        createdByUserId: loadTag.createdByUserId,
        createdDate: loadTag.createdDate,
        name: accountTag.name,
      },
    ]
  })
}

async function postTag({ loadId, tag }: TagRequest): Promise<TagResponse> {
  const endpoint = `${apiUri}/shipments/${loadId}/tags`
  const options = buildFetchOptionsWithAuth({
    body: JSON.stringify({ tag }),
    method: 'POST',
  })

  const response = await fetchJson(endpoint, options)
  if (response.ok) return response.json
  throw response.errorMessage
}

async function deleteTag({ loadId, tag }: TagRequest): Promise<TagResponse> {
  const endpoint = `${apiUri}/shipments/${loadId}/tags/${encodeURIComponent(tag)}`
  const options = buildFetchOptionsWithAuth({
    method: 'DELETE',
  })

  const response = await fetchJson(endpoint, options)
  if (response.ok) return response.json
  throw response.errorMessage
}

const removeTagFromCollection = (
  tags: ShipmentTags | undefined,
  { name, accountTagId }: MakeOptional<ShipmentTag, 'name'>,
): ShipmentTags => {
  if (!tags) return []
  return tags.filter(
    t =>
      (name && t.name !== name) ||
      (accountTagId && t.accountTagId !== accountTagId),
  )
}

const addTagToCollection = (
  tags: ShipmentTags | undefined,
  tag: ShipmentTagOption,
): ShipmentTags => {
  const nextTag: ShipmentTags[number] = {
    accountTagId: tag.value ?? tag.label,
    createdByUserId: '',
    createdDate: new Date().toISOString(),
    name: tag.label,
  }
  if (!tags) return [nextTag]
  return [...tags, nextTag]
}

const useTagMutationHelpers = (loadId: string) => {
  const queryClient = useQueryClient()
  const { modifyAccountTags } = useAccountTagHelpers()

  const modifyQueryTags = (
    fn: (tags: ShipmentTags | undefined) => ShipmentTags,
  ) => {
    queryClient.setQueryData<FullShipment>(
      ['load', loadId],
      prev =>
        prev && {
          ...prev,
          tags: fn(prev.tags),
        },
    )
  }
  const onSuccess = (response: TagResponse) => {
    modifyQueryTags(() => mapTagResponseToShipmentTags(response))
    modifyAccountTags(
      () =>
        response.accountTags?.map(a => ({ label: a.name, _id: a._id })) ?? [],
    )
  }
  return {
    modifyQueryTags,
    onSuccess,
  }
}

const useRemoveMutation = (loadId: string) => {
  const { modifyQueryTags, onSuccess } = useTagMutationHelpers(loadId)
  return useMutation(
    (option: ShipmentTagOption) => deleteTag({ loadId, tag: option.label }),
    {
      onMutate(option) {
        modifyQueryTags(tags =>
          removeTagFromCollection(tags, { accountTagId: option.value }),
        )
      },
      onError(e, optionRemoved) {
        modifyQueryTags(tags => addTagToCollection(tags, optionRemoved))
        toast.error(`Unable to remove tag, ${supportMessage}`)
      },
      onSuccess,
    },
  )
}

const useAddMutation = (loadId: string) => {
  const { modifyQueryTags, onSuccess } = useTagMutationHelpers(loadId)
  const { modifyAccountTags } = useAccountTagHelpers()
  return useMutation(
    (option: ShipmentTagOption) => postTag({ loadId, tag: option.label }),
    {
      onMutate(option) {
        modifyQueryTags(tags => addTagToCollection(tags, option))
      },
      onError(e, option) {
        modifyAccountTags(
          tags => tags?.filter(t => t._id !== option.label) || [], // remove the account tag that was added with an _id of label
        )
        toast.error(`Unable to add tag, ${supportMessage}`)
      },
      onSuccess,
    },
  )
}

export const useShipmentTagMutations = (loadId: string) => {
  const removeMutation = useRemoveMutation(loadId)
  const addMutation = useAddMutation(loadId)
  const cleanTag = (tag: ShipmentTagOption): ShipmentTagOption => {
    const { label, value } = tag
    return {
      label: label.trim(),
      value,
    }
  }
  return {
    removeTag: async (tag: ShipmentTagOption): Promise<TagResponse> =>
      removeMutation.mutateAsync(cleanTag(tag)),
    addTag: async (
      tag: ShipmentTagOption,
    ): Promise<TagResponse | undefined> => {
      const cleaned = cleanTag(tag)
      if (!cleaned.label) {
        toast.error('Invalid tag name')
        return undefined
      }
      return await addMutation.mutateAsync(cleanTag(tag))
    },
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending,
  }
}

export const useAccountTagHelpers = () => {
  const queryClient = useQueryClient()
  const modifyAccountTags = (
    fn: (t: FilterOption[] | undefined) => FilterOption[],
  ) => {
    queryClient.setQueryData<UserSettings>(
      userSettingsKey,
      p =>
        p && {
          ...p,
          tags: fn(p.tags),
        },
    )
  }
  return {
    modifyAccountTags,
  }
}
